/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import { Fragment } from 'react'
import { jsx, css } from '@emotion/react'
import { includes } from 'lodash'
import { parse } from 'query-string'

import { EuiButton, EuiLink, EuiText, EuiThemeComputed, useEuiTheme } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { CuiRouterLinkButton } from '@modules/cui/RouterButton'
import { CuiLink } from '@modules/cui/Link'
import { useConfig } from '@modules/cui/ConfigContext'
import Feature from '@modules/utils/feature'

import {
  buildLoginUrl,
  buildMarketplaceConversionRegisterUrl,
  buildRegisterUrl,
} from '@/lib/urlUtils'
import { marketplaceConversionUrl } from '@/lib/urlBuilder'

import messages from './messages'

import type { FunctionComponent, ReactElement, ReactNode } from 'react'

type LocationState = {
  [key: string]: string | boolean
}

export interface Props {
  locationQueryString: string
  locationState?: LocationState
  onClick?: (() => void) | undefined
}

const getStyles = (euiTheme: EuiThemeComputed) => ({
  loginLink: css({
    marginLeft: `${euiTheme.size.m}`,
    [`@media only screen and (max-width: ${euiTheme.breakpoint.s}px)`]: {
      display: 'none',
    },
  }),
  loginLinkMobile: css({
    display: 'none',
    [`@media only screen and (max-width: ${euiTheme.breakpoint.s}px)`]: {
      display: 'block',
      textAlign: 'right',
      marginLeft: 'auto',
    },
  }),
})

export const LoginButton: FunctionComponent<Props> = ({ locationQueryString, ...restProps }) => {
  const { euiTheme } = useEuiTheme()
  const styles = getStyles(euiTheme)

  return (
    <HeaderButton
      buttonMessage={<FormattedMessage {...messages.login} />}
      descriptionMessage={<FormattedMessage {...messages.loginDescription} />}
      redirectURL={buildLoginUrl({ locationQueryString })}
      styles={styles}
      {...restProps}
    />
  )
}

export const SignupButton: FunctionComponent<Props> = ({ locationQueryString, ...restProps }) => {
  const { euiTheme } = useEuiTheme()
  const styles = getStyles(euiTheme)
  const { fromURI } = parse(locationQueryString.slice(1))
  const redirectURL = includes(fromURI, marketplaceConversionUrl())
    ? buildMarketplaceConversionRegisterUrl({ search: locationQueryString })
    : buildRegisterUrl({ search: locationQueryString })

  const registrationEnabled = Boolean(useConfig(Feature.registration))

  if (!registrationEnabled) {
    return null
  }

  return (
    <HeaderButton
      buttonMessage={<FormattedMessage {...messages.signup} />}
      descriptionMessage={<FormattedMessage {...messages.signupDescription} />}
      redirectURL={redirectURL}
      styles={styles}
      {...restProps}
    />
  )
}

export const LogoutButton: FunctionComponent<Props> = ({ ...restProps }) => {
  const { euiTheme } = useEuiTheme()
  const styles = getStyles(euiTheme)

  return (
    <HeaderButton
      buttonMessage={<FormattedMessage {...messages.logout} />}
      styles={styles}
      {...restProps}
      redirectURL='/logout'
    />
  )
}

const HeaderButton: FunctionComponent<{
  buttonMessage?: ReactNode
  descriptionMessage?: ReactNode
  onClick?: (() => void) | undefined
  redirectURL: string
  locationState?: LocationState
  styles: ReturnType<typeof getStyles>
}> = ({
  buttonMessage,
  descriptionMessage,
  onClick,
  redirectURL,
  locationState,
  styles,
}): ReactElement => {
  const [pathname, search] = redirectURL.split('?')
  const linkTo = { pathname, search, state: locationState }
  return (
    <Fragment>
      <EuiText textAlign='right' size='s' css={styles.loginLink}>
        {descriptionMessage} {getButton()}
      </EuiText>

      {getMobileLink()}
    </Fragment>
  )

  function getButton() {
    const buttonProps = {
      fill: true,
      css: styles.loginLink,
    }

    if (onClick) {
      return (
        <EuiButton
          {...buttonProps}
          size='s'
          onClick={onClick}
          data-test-id='cloud-landing-page.login-button'
        >
          {buttonMessage}
        </EuiButton>
      )
    }

    return (
      <CuiRouterLinkButton
        {...buttonProps}
        size='s'
        to={linkTo}
        data-test-id='cloud-landing-page.login-link'
      >
        {buttonMessage}
      </CuiRouterLinkButton>
    )
  }

  function getMobileLink() {
    const mobileLinkProps = {
      css: styles.loginLinkMobile,
    }

    if (onClick) {
      return (
        <EuiLink {...mobileLinkProps} onClick={onClick}>
          {buttonMessage}
        </EuiLink>
      )
    }

    return (
      <CuiLink {...mobileLinkProps} to={linkTo} data-test-id='cloud-landing-page.login-link'>
        {buttonMessage}
      </CuiLink>
    )
  }
}
